import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { marketingAnalytics, analytics, reportOps } from "../lib/supabase.js";

// ═══ FunnelOverview — the whole customer journey in sales language ═══
// Google → website → assistant → lead, with plain-English terms throughout
// and a direct bridge into Training for questions the assistant fumbled.

export const GLOSSARY = {
  "Impressions": "How many times MORryde showed up in Google search results. Someone searched, and we appeared on their screen — whether or not they clicked.",
  "Clicks": "How many times someone clicked through from Google to the website. Free traffic from search.",
  "Sessions": "Visits to the website. One person coming back twice counts as two sessions.",
  "Visitors": "Individual people on the site (as best Google can tell). One person = one visitor, no matter how many visits.",
  "Engaged visits": "Visits where the person actually did something — stayed a while, viewed more than one page, or clicked around. The opposite of a drive-by.",
  "Pageviews": "Total pages looked at, across all visits.",
  "Bounce rate": "The share of visits where someone landed, looked at one page, and left. Lower is better.",
  "CTR": "Click-through rate: of everyone who saw us in Google, the percentage who clicked. 6% means 6 clicks per 100 appearances.",
  "Avg position": "Where we typically rank in Google results. Position 1 is the top result; under 10 means page one.",
  "Conversations": "Visitors who opened the chat assistant and sent at least one message.",
  "Leads": "People who gave their name and contact info through the assistant — real follow-up opportunities for the sales team.",
  "Escalations": "Conversations the assistant handed to a human — usually because someone asked for a person or the question needed one.",
  "Conversion rate": "The percentage of people who moved from one step to the next. Every funnel leaks; the question is where it leaks most.",
};

export function Term({ children, def }) {
  const [show, setShow] = useState(false);
  const definition = def || GLOSSARY[children];
  if (!definition) return <span>{children}</span>;
  return (
    <span style={{ position: "relative", borderBottom: "1px dotted #999", cursor: "help" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <span style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
          width: 260, background: "#1a1a1a", color: "#f0f0ec", fontSize: 11.5, lineHeight: 1.55, padding: "10px 12px",
          borderRadius: 8, zIndex: 60, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", textTransform: "none",
          letterSpacing: "normal", fontWeight: 400, whiteSpace: "normal", pointerEvents: "none" }}>
          {definition}
        </span>
      )}
    </span>
  );
}

export function GlossaryPanel({ open, onClose }) {
  if (!open) return null;
  return (
    <div style={{ border: "1px solid #e5e3de", borderRadius: 12, padding: "20px 24px", marginBottom: 20, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8a8a8a" }}>Plain-English glossary</span>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#8a8a8a", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>Close</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 32px" }}>
        {Object.entries(GLOSSARY).map(([term, def]) => (
          <div key={term} style={{ fontSize: 12.5, lineHeight: 1.55 }}>
            <span style={{ fontWeight: 700 }}>{term}</span> <span style={{ color: "#6a6a6a" }}>— {def}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmt = (n) => { const v = parseFloat(n) || 0; return v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toLocaleString(); };
const pct = (a, b) => { const A = parseFloat(a) || 0, B = parseFloat(b) || 0; return B > 0 ? (A / B) * 100 : 0; };
const fmtPct = (v) => v >= 10 ? `${Math.round(v)}%` : v >= 1 ? `${v.toFixed(1)}%` : v > 0 ? `${v.toFixed(2)}%` : "0%";
const iso = (d) => d.toISOString().slice(0, 10);

export default function FunnelOverview({ days = 30, color = "#6366f1" }) {
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [ga, setGa] = useState({});
  const [gsc, setGsc] = useState({});
  const [struggles, setStruggles] = useState([]);
  const [showGlossary, setShowGlossary] = useState(false);
  const [fa, setFa] = useState(null);
  const [faLoading, setFaLoading] = useState(false);

  const analyzeFunnel = async () => {
    setFaLoading(true); setFa(null);
    const end2 = iso(new Date());
    const start2 = iso(new Date(Date.now() - (days - 1) * 86400000));
    const d = await reportOps("analyze", { start: start2, end: end2, metrics: ["funnel", "ga4_summary", "gsc_summary", "ga4_daily", "weak_spots"] });
    setFaLoading(false);
    setFa(d.error ? { error: d.error } : { sections: d.analysis || [] });
  };

  useEffect(() => {
    (async () => {
      const end = iso(new Date());
      const start = iso(new Date(Date.now() - (days - 1) * 86400000));
      const [{ data: funnel }, { data: gaS }, { data: gscS }, lowMatch] = await Promise.all([
        marketingAnalytics.funnelRange(start, end),
        marketingAnalytics.ga4SummaryRange(start, end),
        marketingAnalytics.gscSummaryRange(start, end),
        analytics.lowMatchQueries ? analytics.lowMatchQueries(days) : Promise.resolve({ data: [] }),
      ]);
      setF((Array.isArray(funnel) ? funnel[0] : funnel) || {});
      setGa((Array.isArray(gaS) ? gaS[0] : gaS) || {});
      setGsc((Array.isArray(gscS) ? gscS[0] : gscS) || {});
      setStruggles((lowMatch?.data || []).slice(0, 6));
    })();
  }, [days]);

  if (!f) return null;
  const stages = [
    { key: "impressions", label: "Found on Google", term: "Impressions", value: f.impressions, hint: "People who saw MORryde in search results" },
    { key: "clicks", label: "Clicked through", term: "Clicks", value: f.clicks, hint: "Came to the website from Google" },
    { key: "sessions", label: "Visited the site", term: "Sessions", value: f.sessions, hint: "All website visits, from every source" },
    { key: "engaged", label: "Stuck around", term: "Engaged visits", value: f.engaged_sessions, hint: "Actually explored instead of bouncing" },
    { key: "chats", label: "Talked to the assistant", term: "Conversations", value: f.chat_sessions, hint: "Opened the chat and asked something" },
    { key: "leads", label: "Became a lead", term: "Leads", value: f.leads, hint: "Left contact info for follow-up" },
  ].map(s => ({ ...s, v: parseFloat(s.value) || 0 }));

  const hasAny = stages.some(s => s.v > 0);
  if (!hasAny) return null;
  const maxV = Math.max(1, ...stages.map(s => s.v));

  // Biggest leak: largest absolute drop between adjacent populated stages
  let leak = null;
  for (let i = 1; i < stages.length; i++) {
    if (stages[i - 1].v > 0) {
      const rate = pct(stages[i].v, stages[i - 1].v);
      if (!leak || rate < leak.rate) leak = { from: stages[i - 1], to: stages[i], rate };
    }
  }

  const card = { background: "#fff", border: "1px solid #e5e3de", borderRadius: 12, padding: "22px 24px" };

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8a8a8a" }}>
          The customer journey &middot; last {days} days
        </div>
        <button onClick={analyzeFunnel} style={{ background: "none", border: "none", color: "#6366f1", fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit", marginRight: 14 }}>
          {faLoading ? "Analyzing\u2026" : "Analyze the funnel"}
        </button>
        <button onClick={() => setShowGlossary(g => !g)} style={{ background: "none", border: "none", color: "#555", fontSize: 12, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit" }}>
          What do these terms mean?
        </button>
      </div>
      <GlossaryPanel open={showGlossary} onClose={() => setShowGlossary(false)} />

      {/* ── Website & search at a glance ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        {[
          { t: "Sessions", label: "Website visits", v: fmt(ga.sessions) },
          { t: "Visitors", label: "People on the site", v: fmt(ga.users) },
          { t: "Pageviews", label: "Pages viewed", v: fmt(ga.pageviews) },
          { t: "Bounce rate", label: "Left after one page", v: ga.avg_bounce_rate != null ? `${(parseFloat(ga.avg_bounce_rate) * 100).toFixed(0)}%` : "\u2014" },
          { t: "Impressions", label: "Seen in Google", v: fmt(gsc.impressions) },
          { t: "Clicks", label: "Clicked from Google", v: fmt(gsc.clicks) },
          { t: "CTR", label: "See us \u2192 click us", v: gsc.avg_ctr != null ? `${(parseFloat(gsc.avg_ctr) * 100).toFixed(1)}%` : "\u2014" },
          { t: "Avg position", label: "Typical Google rank", v: gsc.avg_position ? parseFloat(gsc.avg_position).toFixed(1) : "\u2014" },
        ].map(c => (
          <div key={c.label} style={{ ...card, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#a0a0a0", marginBottom: 6 }}>
              <Term def={GLOSSARY[c.t]}>{c.label}</Term>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{c.v}</div>
          </div>
        ))}
      </div>

      {/* ── The funnel ── */}
      <div style={card}>
        {stages.map((s, i) => {
          const conv = i > 0 && stages[i - 1].v > 0 ? pct(s.v, stages[i - 1].v) : null;
          const isLeak = leak && leak.to.key === s.key && leak.rate < 50;
          return (
            <div key={s.key} style={{ display: "grid", gridTemplateColumns: "190px 1fr 90px 130px", gap: 14, alignItems: "center", padding: "11px 0", borderTop: i > 0 ? "1px solid #f0eee9" : "none" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}><Term def={GLOSSARY[s.term]}>{s.label}</Term></div>
                <div style={{ fontSize: 10.5, color: "#a0a0a0", marginTop: 2 }}>{s.hint}</div>
              </div>
              <div style={{ height: 22, background: "#f5f4f0", borderRadius: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.max(0.6, (s.v / maxV) * 100)}%`, background: isLeak ? "#c0503f" : color, opacity: 0.85, borderRadius: 5, transition: "width 0.5s ease" }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, textAlign: "right" }}>{fmt(s.v)}</div>
              <div style={{ fontSize: 11.5, color: isLeak ? "#c0503f" : "#8a8a8a", textAlign: "right", fontWeight: isLeak ? 700 : 400 }}>
                {conv != null ? <><Term def={GLOSSARY["Conversion rate"]}>{fmtPct(conv)}</Term> of previous</> : "starting point"}
              </div>
            </div>
          );
        })}
        {leak && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f0eee9", fontSize: 12.5, color: "#555", lineHeight: 1.6 }}>
            <span style={{ fontWeight: 700, color: "#c0503f" }}>Biggest leak:</span>{" "}
            only {fmtPct(leak.rate)} of people who {leak.from.label.toLowerCase()} go on to {leak.to.label.toLowerCase()}.
            {leak.to.key === "chats" && " Making the chat assistant more visible or opening it proactively on key pages would work directly on this number."}
            {leak.to.key === "leads" && " Training the assistant to offer the contact form at the right moments would work directly on this number."}
            {leak.to.key === "clicks" && " We show up in search but people aren't clicking — better page titles and descriptions for top queries would work directly on this number."}
            {(leak.to.key === "sessions" || leak.to.key === "engaged") && " Getting landing pages to match what people searched for would work directly on this number."}
          </div>
        )}
      </div>

      {fa && (
        <div style={{ ...card, marginTop: 16, borderColor: "#c9c9f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#6366f1" }}>Funnel analysis</span>
            <button onClick={() => setFa(null)} style={{ background: "none", border: "none", color: "#8a8a8a", fontSize: 12, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Dismiss</button>
          </div>
          {fa.error && <div style={{ fontSize: 12.5, color: "#c0503f" }}>{fa.error}</div>}
          {fa.sections?.map((sec, i) => (
            <div key={i} style={{ marginBottom: i < fa.sections.length - 1 ? 12 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{sec.title}</div>
              <div style={{ fontSize: 12.5, color: "#555", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{sec.content}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Training bridge ── */}
      {struggles.length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8a8a8a" }}>Coach your assistant</div>
            <button onClick={() => navigate("/training")} style={{ background: "none", border: "none", color: "#555", fontSize: 12, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, fontFamily: "inherit" }}>Open Training</button>
          </div>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#8a8a8a", lineHeight: 1.5 }}>
            Real questions from real visitors that the assistant struggled to answer well. Click one to teach it the right answer &mdash; that answer then fires instantly for everyone who asks.
          </p>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {struggles.map((q, i) => (
              <div key={i} onClick={() => navigate(`/training?train=${encodeURIComponent(q.query_text || q.query || "")}`)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 2px", borderTop: i > 0 ? "1px solid #f0eee9" : "none", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.55"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                <span style={{ fontSize: 13 }}>{q.query_text || q.query}</span>
                <span style={{ fontSize: 11.5, color: "#6366f1", fontWeight: 600, whiteSpace: "nowrap" }}>Train this answer &rarr;</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
